<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'
import authApi from '@/api/modules/auth'

defineOptions({
  name: 'FaForceChangePassword',
})

const userStore = useUserStore()
const { needChangePassword, isLogin, displayName } = storeToRefs(userStore)

const visible = ref(false)
const loading = ref(false)
const formRef = ref<FormInstance>()
const formModel = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const rules: FormRules = {
  oldPassword: [{ required: true, message: '请输入当前密码', trigger: 'blur' }],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码至少 6 位', trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    {
      validator: (_rule, value, callback) => {
        if (value !== formModel.newPassword) {
          callback(new Error('两次输入的密码不一致'))
        }
        else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
}

function resetForm() {
  formRef.value?.clearValidate()
  formModel.oldPassword = ''
  formModel.newPassword = ''
  formModel.confirmPassword = ''
}

watch(
  [needChangePassword, isLogin],
  ([needChange, login]) => {
    if (needChange && login) {
      visible.value = true
    }
    else if (!needChange) {
      visible.value = false
      resetForm()
    }
  },
  { immediate: true },
)

async function handleConfirm() {
  if (!formRef.value) {
    return
  }

  try {
    const valid = await formRef.value.validate().catch(() => false)
    if (!valid) {
      return
    }

    loading.value = true
    await authApi.changePassword({
      oldPassword: formModel.oldPassword,
      password: formModel.newPassword,
    })
    ElMessage.success('密码修改成功')
    userStore.setNeedChangePassword(false)
    visible.value = false
    resetForm()
  }
  catch (error: any) {
    if (error?.message) {
      ElMessage.error(error.message)
    }
    else {
      ElMessage.error('密码修改失败')
    }
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <FaModal
    v-model="visible"
    :header="false"
    :footer="false"
    :closable="false"
    :close-on-click-overlay="false"
    :close-on-press-escape="false"
    custom-class="force-change-password-modal"
  >
    <div class="force-change-password-panel">
      <div class="force-change-password-header">
        <div class="force-change-password-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 2a5 5 0 0 0-5 5v2H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm3 7H9V7a3 3 0 1 1 6 0v2Zm-3 4a2 2 0 1 1-2 2a2 2 0 0 1 2-2Z"
            />
          </svg>
        </div>
        <div class="force-change-password-title">
          <h3>
            {{ displayName || '尊敬的用户' }}，请立即修改密码
          </h3>
          <p>
            为确保账号安全，建议设置一个仅自己知晓且至少 6 位的复杂密码。
          </p>
        </div>
      </div>
      <ElAlert
        type="info"
        :closable="false"
        class="force-change-password-alert"
        description="新密码建议同时包含数字、字母和符号。"
      />
      <ElForm
        ref="formRef"
        :model="formModel"
        :rules="rules"
        label-position="top"
        class="force-change-password-form"
      >
        <ElFormItem label="当前密码" prop="oldPassword">
          <ElInput
            v-model="formModel.oldPassword"
            type="password"
            placeholder="请输入当前密码"
            show-password
          />
        </ElFormItem>
        <ElDivider class="force-change-password-divider" />
        <ElFormItem label="新密码" prop="newPassword">
          <ElInput
            v-model="formModel.newPassword"
            type="password"
            placeholder="请输入新密码"
            show-password
          />
        </ElFormItem>
        <ElFormItem label="确认新密码" prop="confirmPassword">
          <ElInput
            v-model="formModel.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
          />
        </ElFormItem>
        <div class="force-change-password-actions">
          <ElButton
            type="primary"
            :loading="loading"
            @click="handleConfirm"
          >
            保存新密码
          </ElButton>
        </div>
      </ElForm>
    </div>
  </FaModal>
</template>

<style scoped>
.force-change-password-modal :deep(.el-dialog__body) {
  padding: 0;
}

.force-change-password-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 32px 28px;
  border-radius: 20px;
}

.force-change-password-header {
  display: flex;
  gap: 16px;
  align-items: center;
}

.force-change-password-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  color: var(--el-color-primary);
  background: linear-gradient(135deg, rgb(24 144 255 / 16%), rgb(24 144 255 / 40%));
  border-radius: 16px;
}

.force-change-password-icon svg {
  width: 28px;
  height: 28px;
}

.force-change-password-title h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.force-change-password-title p {
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--el-text-color-secondary);
}

.force-change-password-alert {
  color: var(--el-text-color-primary);
  background-color: rgb(24 144 255 / 6%);
  border: none;
  border-radius: 12px;
}

.force-change-password-alert :deep(.el-alert__description) {
  font-size: 13px;
  line-height: 1.5;
}

.force-change-password-form :deep(.el-form-item__label) {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.force-change-password-form :deep(.el-input__wrapper) {
  border-radius: 12px;
}

.force-change-password-divider {
  margin: 8px 0 0;
}

.force-change-password-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.force-change-password-actions :deep(.el-button) {
  min-width: 120px;
  border-radius: 999px;
}
</style>
